import * as React from 'react';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  TextInput,
  required,
  AutocompleteInput,
  useEditContext,
  Toolbar,
  SaveButton,
  ReferenceInput,
  ReferenceField,
  SelectInput,
  FormDataConsumer,
  FunctionField,
  useTranslate,
} from 'react-admin';
import { useWatch } from 'react-hook-form';

// Components
import EmptyList from '../components/misc/EmptyList';
import { SeatingPlanEditor } from '../components/seating';
import { FirebasePushButton } from '../components/misc/FirebasePushButton';

// UI
import { Box } from '@mui/material';

// Entity
const entity = 'layouts';

/** Form/API icin layout JSON string'e cevir. */
const serializeLayout = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

const getLayoutTypes = (translate) => [
  { id: 'seating', name: translate('layouts.seat_plan', { _: 'Koltuklu Oturma Planı' }) },
  { id: 'area', name: translate('layouts.area_layout', { _: 'Alan / Ayakta Düzen' }) },
];

const LayoutFirebasePushBar = ({ record }) => {
  const translate = useTranslate();
  const layoutValue = useWatch({ name: 'layout' });
  const nameValue = useWatch({ name: 'name' });
  const pushRecord = {
    ...record,
    name: nameValue || record?.name,
    layout: layoutValue != null && layoutValue !== '' ? layoutValue : record?.layout,
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <FirebasePushButton mode="layout" record={pushRecord} />
      <span style={{ marginLeft: 10, fontSize: 12, color: '#b45309' }}>
        {translate('layouts.firebase_temp_note', { _: 'Geçici: aynı id ile Firebase layouts/ üzerine yazar' })}
      </span>
    </div>
  );
};

export const LayoutList = (props) => {
  const translate = useTranslate();
  const layoutTypes = getLayoutTypes(translate);

  return (
    <List {...props} title={translate('layouts.title', { _: 'Mekan Yerleşimleri / Düzenler' })} empty={<EmptyList entity={entity} />} exporter={false} perPage={50}>
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" label={translate('layouts.name', { _: 'Plan Adı' })} />
        <ReferenceField source="venue" reference="venues" label={translate('layouts.venue', { _: 'Mekan' })}>
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          source="layoutType"
          label={translate('layouts.type', { _: 'Tür' })}
          render={(r) => {
            const layoutType = layoutTypes.find((l) => l.id === r.layoutType);
            return layoutType ? layoutType.name : '';
          }}
        />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const LayoutEditToolbar = (props) => {
  const translate = useTranslate();
  return (
    <Toolbar {...props}>
      <SaveButton
        label={translate('ra.action.save', { _: 'Kaydet' })}
        transform={(data) => {
          const layout = serializeLayout(data.layout);
          return { ...data, notify: true, layout };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const LayoutEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit title={translate('layouts.edit_layout', { _: 'Yerleşim Planını Düzenle' })} {...props} actions={false}>
      <LayoutEditForm />
    </Edit>
  );
};

const LayoutEditForm = () => {
  const translate = useTranslate();
  const { record } = useEditContext();

  return (
    <SimpleForm redirect={`/${entity}`} toolbar={<LayoutEditToolbar />}>
      <TextInput source="layout" sx={{ display: 'none' }} />
      {record?.id ? <LayoutFirebasePushBar record={record} /> : null}
      <TextInput source="name" label={translate('layouts.name', { _: 'Plan Adı' })} fullWidth />
      <ReferenceInput
        source="venue"
        reference="venues"
        label={translate('layouts.venue', { _: 'Mekan' })}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteInput
          optionText="name"
          fullWidth
          matchSuggestion={() => true}
          filterToQuery={(searchText) => ({ name: searchText })}
          validate={required()}
          disabled
        />
      </ReferenceInput>
      {record && record.id ? (
        <React.Fragment>
          {!record.layout ? (
            <div style={{ marginBottom: 10, color: 'red' }}>
              {translate('layouts.no_data_error', { _: 'Bu layout için kayıtlı oturma planı verisi yok.' })}
            </div>
          ) : (
            <SeatingPlanEditor
              record={record}
              layoutType={record.layoutType || 'seating'}
              isLayoutEdit={true}
              isCreateMode={true}
            />
          )}
        </React.Fragment>
      ) : (
        <div style={{ marginBottom: 10, color: 'red' }}>Layout yüklenirken hata oluştu.</div>
      )}
    </SimpleForm>
  );
};

const LayoutCreateToolbar = (props) => {
  const translate = useTranslate();
  return (
    <Toolbar {...props}>
      <SaveButton
        label={translate('ra.action.save', { _: 'Kaydet' })}
        transform={(data) => {
          // Plan react-hook-form 'layout' alaninda; localStorage eski Firebase kalintisi.
          const legacy = localStorage.getItem('seatingPlan');
          if (legacy) localStorage.removeItem('seatingPlan');
          const layout = serializeLayout(legacy ?? data.layout);
          return { ...data, notify: true, layout };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const LayoutCreate = (props) => {
  const translate = useTranslate();
  const layoutTypes = getLayoutTypes(translate);

  return (
    <Create title={translate('layouts.new_layout', { _: 'Yeni Yerleşim Planı' })} {...props} redirect={'list'}>
      <SimpleForm redirect={'list'} toolbar={<LayoutCreateToolbar />}>
        <TextInput source="layout" sx={{ display: 'none' }} defaultValue={null} />
        <TextInput source="name" label={translate('layouts.name', { _: 'Plan Adı' })} fullWidth validate={required()} />

        <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
          <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
            <SelectInput
              source="layoutType"
              label={translate('layouts.type', { _: 'Tür' })}
              choices={layoutTypes}
              fullWidth
              defaultValue={'seating'}
              validate={required()}
            />
          </Box>
          <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
            <ReferenceInput
              source="venue"
              reference="venues"
              label={translate('layouts.venue', { _: 'Mekan' })}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteInput
                optionText="name"
                fullWidth
                matchSuggestion={() => true}
                filterToQuery={(searchText) => ({ name: searchText })}
                validate={required()}
              />
            </ReferenceInput>
          </Box>
        </Box>

        <FormDataConsumer>
          {({ formData }) => (
            <SeatingPlanEditor
              record={props.record}
              layoutType={formData.layoutType}
              isAssignMode={false}
              isCreateMode={true}
            />
          )}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  );
};
